import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BottomProps,
  HeightProps,
  LeftProps,
  MaxWidthProps,
  PositionProps,
  RightProps,
  SpaceProps,
  TopProps,
  WidthProps,
} from 'styled-system';
import { bottom, height, left, maxWidth, position, right, space, top, width } from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { FlexProps } from 'common/components/Flex';
import { GRID_MAX_WIDTH } from 'common/constants/grid';
import { breakpoint } from 'common/theme/helper';
import type { BoxSizingProps, PointerEventProps } from 'common/theme/system-utilities';
import { boxSizing, pointerEvents } from 'common/theme/system-utilities';

const Overlay = styled.div<
  PositionProps & TopProps & LeftProps & RightProps & BottomProps & PointerEventProps
>`
  ${position};
  ${top};
  ${left};
  ${right};
  ${bottom};
  ${pointerEvents};
`;

const overlayStyles: ComponentProps<typeof Overlay> = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
};

const Constraint = styled.div<
  MaxWidthProps & SpaceProps & HeightProps & BoxSizingProps & WidthProps
>`
  ${maxWidth};
  ${space};
  ${height};
  ${boxSizing};
  ${width};

  ${breakpoint(0)} {
    padding-left: 24px;
    padding-right: 24px;
  }

  ${breakpoint(1)} {
    padding-left: 48px;
    padding-right: 48px;
  }

  ${breakpoint(2)} {
    padding-left: 64px;
    padding-right: 64px;
  }
`;

const constraintStyles: ComponentProps<typeof Constraint> = {
  maxWidth: GRID_MAX_WIDTH,
  mx: 'auto',
  height: '100%',
  boxSizing: 'border-box',
  width: 1,
  px: 'sp_16',
};

const Inner = styled(Flex)<FlexProps & PointerEventProps>`
  ${pointerEvents};

  & > * {
    pointer-events: auto;
  }
`;

const innerStyles: ComponentProps<typeof Inner> = {
  position: 'relative',
  alignItems: 'center',
  height: '100%',
  pointerEvents: 'none',
};

const GridConstraintOverlay = ({ children }: PropsWithChildren) => (
  <Overlay {...overlayStyles} data-testid="grid-constraint-overlay">
    <Constraint {...constraintStyles} data-testid="grid-constraint">
      <Inner {...innerStyles} data-testid="grid-constraint-inner">
        {children}
      </Inner>
    </Constraint>
  </Overlay>
);

export default GridConstraintOverlay;
